import { useState, useEffect, useCallback, useRef } from 'react';

function handleUnauthorized() {
  try {
    localStorage.removeItem('fin_sso_user');
    localStorage.removeItem('fin_auth_token');
    localStorage.removeItem('fin_auth_user');
  } catch {}
  window.location.reload();
}

interface UseFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Simple data-fetching hook to replace Next.js useCachedFetch
 */
export function useFetch<T>(url: string, options?: RequestInit): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef(url);
  urlRef.current = url;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers as Record<string, string> ?? {}),
      };
      const res = await fetch(urlRef.current, { ...options, headers });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  }, [options]);

  useEffect(() => {
    fetchData();
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, refresh: fetchData };
}

/**
 * Fetch with manual trigger (no auto-fetch on mount)
 */
export function useMutation<TBody = any, TResult = any>(url: string, method: string = 'POST') {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (body?: TBody): Promise<TResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.status === 401) {
        handleUnauthorized();
        return null;
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      return json as TResult;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [url, method]);

  return { mutate, loading, error };
}
